// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Waves.tsx
//  Entregas → Separação por Wave (lista + montagem). Porte de FrmSeparacaoWave.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Layers, PackageCheck } from 'lucide-react';

import { waveService } from '@/features/operacional/services/waveService';
import { WAVE_STATUS } from '@/features/operacional/types';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const STATUS_COR: Record<number, string> = {
  0: 'bg-zinc-500/10 text-zinc-500', 1: 'bg-blue-500/10 text-blue-600',
  2: 'bg-amber-500/10 text-amber-600', 3: 'bg-emerald-500/10 text-emerald-600',
  4: 'bg-rose-500/10 text-rose-600',
};

function fdata(iso?: string | null) {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
}

const Waves = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: waves = [], isLoading } = useQuery({ queryKey: ['waves'], queryFn: () => waveService.getAll() });

  const [open, setOpen] = useState(false);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [serpentina, setSerpentina] = useState(false);
  const [observacao, setObservacao] = useState('');

  const { data: disponiveis = [], isFetching } = useQuery({
    queryKey: ['waves-disponiveis'],
    queryFn: () => waveService.disponiveis(),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () => waveService.create({
      movDavIds: Array.from(selecionados), serpentina, observacao: observacao || null,
    }),
    onSuccess: (w) => {
      queryClient.invalidateQueries({ queryKey: ['waves'] });
      toast.success('Wave criada.');
      setOpen(false);
      navigate(`/operacional/waves/${w.id}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const toggle = (id: number) => setSelecionados((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const abrir = () => { setSelecionados(new Set()); setSerpentina(false); setObservacao(''); setOpen(true); };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="h-6 w-6" /> Separação por Wave
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Consolide vários pedidos numa coleta única (picking) e confira o empacotamento (packing).
          </p>
        </div>
        <Button onClick={abrir} className="gap-2"><Plus className="h-4 w-4" /> Nova Wave</Button>
      </div>

      <Card>
        <CardContent className="pt-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Carregando...</p>
          ) : waves.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Nenhuma wave criada.</p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Wave</TableHead>
                    <TableHead className="w-32">Status</TableHead>
                    <TableHead className="w-24 text-right">Pedidos</TableHead>
                    <TableHead>Criada</TableHead>
                    <TableHead>Separação</TableHead>
                    <TableHead>Observação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {waves.map((w) => (
                    <TableRow key={w.id} className="cursor-pointer hover:bg-accent/50" onClick={() => navigate(`/operacional/waves/${w.id}`)}>
                      <TableCell className="font-medium">#{w.id}</TableCell>
                      <TableCell><Badge variant="secondary" className={STATUS_COR[w.status]}>{WAVE_STATUS[w.status] ?? w.status}</Badge></TableCell>
                      <TableCell className="text-right">{w.qtdPedidos}</TableCell>
                      <TableCell className="text-sm">{fdata(w.dtCriacao)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fdata(w.dtInicioSeparacao)} – {fdata(w.dtFimSeparacao)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{w.observacao ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nova wave: seleção de pedidos disponíveis */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PackageCheck className="h-5 w-5" /> Nova Wave</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Selecione os pedidos confirmados (não entregues) para consolidar na coleta.
            </p>
            <div className="rounded-md border max-h-72 overflow-y-auto">
              {isFetching ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Carregando pedidos...</p>
              ) : disponiveis.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Nenhum pedido disponível.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10" />
                      <TableHead className="w-20">DAV</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="w-20 text-right">Itens</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {disponiveis.map((d) => (
                      <TableRow key={d.movDavId} className="cursor-pointer" onClick={() => toggle(d.movDavId)}>
                        <TableCell><Checkbox checked={selecionados.has(d.movDavId)} onCheckedChange={() => toggle(d.movDavId)} /></TableCell>
                        <TableCell className="font-medium">#{d.numeroDav || d.movDavId}</TableCell>
                        <TableCell className="text-sm">{d.clienteNome ?? '—'}</TableCell>
                        <TableCell className="text-right text-sm">{d.qtdItens}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Checkbox id="serp" checked={serpentina} onCheckedChange={(v) => setSerpentina(!!v)} />
              <Label htmlFor="serp" className="font-normal cursor-pointer">Rota em serpentina (otimizar coleta)</Label>
            </div>
            <div className="space-y-1">
              <Label>Observação</Label>
              <Input value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={200} />
            </div>
            <p className="text-sm text-muted-foreground">{selecionados.size} pedido(s) selecionado(s).</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => create.mutate()} disabled={selecionados.size === 0 || create.isPending}>
              Criar Wave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Waves;
